<template>
  <div class="package-panel">
    <slot name="summary-header" />

    <div class="package-info">
      <hr class="package-info__divider" />
      <h3 class="package-info-header">
        Your package includes
      </h3>
      <ul class="package-panel__products">
        <li
          class="package-panel__option"
          v-for="(product, index) in filteredSelectedProducts"
          :key="product.code"
          :class="{ disabled: isCellDisabled(product) }"
          data-testid="package-panel-option"
        >
          <!-- Left: icon -->
          <div class="package-panel__option-image-wrapper">
            <img
              v-if="product.type !== 'Broadband' || product.enabled"
              class="package-panel__option-image"
              :src="productImage(product)"
              :alt="product.title"
            />

            <!-- wifi-slash icon if - I don’t need Broadband -->
            <i
              v-else-if="product.type === 'Broadband' && !product.enabled"
              class="fa-solid fa-wifi-slash no-broadband-icon"
              title="No broadband selected"
            ></i>
          </div>

          <!-- Middle: title + price -->
          <div class="package-panel__option-details">
            <div class="package-panel__option-header">
              <h1
                class="package-panel__option-name"
                :data-testid="product.type"
              >
                {{ showProductTitle(product.type) }}
              </h1>
              <InfoBubble
                v-if="product.type !== 'Broadband'"
                :text="productInfoLabel(product)"
                iconColor="#3B4143"
              />
            </div>
            <p
              class="package-panel__option-price"
              v-if="
                index !== 0 && (product.type !== 'Broadband' || product.enabled)
              "
              :data-testid="'current-price-' + product.type"
            >
              +£{{ (Math.ceil(product.price.current) / 100).toFixed(2) }} per
              person per week
            </p>
          </div>

          <!-- Right: action -->
          <div v-if="index !== 0" class="package-panel__option-action">
            <i
              v-if="isBroadband(product)"
              class="fa-solid fa-square-pen edit-broadband"
              data-testid="edit-broadband"
              @click="openBroadbandModal(product)"
              style="cursor:pointer;margin-right:2px;"
            ></i>

            <input
              v-else
              type="checkbox"
              class="form-checkbox"
              :checked="product.enabled"
              :data-testid="'checkbox_' + product.type"
              @change="
                () => {
                  updateProductSelection(product);
                  onToggleProducts();
                }
              "
            />
          </div>
        </li>
      </ul>
    </div>

    <!-- Broadband modal -->
    <broadband-selector-modal
      :visible="showBroadbandModal"
      :products="broadbandProducts"
      :selected="selectedBroadband"
      @option-selected="updateSelected"
      @price-changed="priceChanged"
      @close="closeBroadbandModal"
    />
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import ProductInfoOverlay from "@/components/ProductInfoOverlay.vue";
import InfoBubble from "@/components/InfoBubble.vue";
import BroadbandSelectorModal from "@/components/modals/BroadbandSelectorModal.vue";

export default {
  name: "PackageSummaryNew",
  components: { BroadbandSelectorModal, InfoBubble, ProductInfoOverlay },
  data() {
    return {
      infoPanelShowing: false,
      openInfoProductCode: null,
      errorResponse: null,
      isBroadbandModalOpen: false,
      editingProduct: null,
      showBroadbandModal: false,
      selectedProduct: null,
    };
  },
  props: {
    thin: {
      type: Boolean,
      required: false,
      default: false,
    },
    joiningTenant: {
      type: Boolean,
      required: false,
      default: false,
    },
    inlineEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideUnwantedProducts: {
      type: Boolean,
      required: false,
      default: true,
    },
    onToggleProducts: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    ...mapState({
      products({ products }) {
        return products;
      },
      selectedProducts({ accountInformation }) {
        if (this.joiningTenant) {
          return accountInformation.products;
        } else {
          return this.showOptionalProducts
            ? this.$store.getters.selectedUnselectedProducts
            : this.$store.getters.selectedProducts;
        }
      },
    }),
    filteredSelectedProducts() {
      const orderedTypes = ["Energy", "Water", "Broadband", "TV Licence"];

      const mappedProducts = this.selectedProducts.map((product) => {
        const baseProduct = this.products.find((p) => p.type === product.type);

        if (!baseProduct) return product;

        let selectedOption = baseProduct.options.find(
          (op) => op.code === product.code,
        );

        if (product.code.includes("_NONE")) {
          selectedOption = baseProduct.options.find(
            (op) => !op.code.includes("_NONE"),
          );
        }

        return {
          ...product,
          title: baseProduct.title,
          image:
            (selectedOption && selectedOption.image) || baseProduct.image || "",
          price: (selectedOption && selectedOption.price) ||
            baseProduct.price || { current: 0 },
          enabled: !product.code.includes("_NONE"),
        };
      });

      return mappedProducts.sort(
        (a, b) => orderedTypes.indexOf(a.type) - orderedTypes.indexOf(b.type),
      );
    },
    broadbandProducts() {
      const broadband = this.products.find((p) => p.type === "Broadband");
      return broadband ? broadband.options : [];
    },
    selectedBroadband() {
      const broadband = this.products.find((p) => p.type === "Broadband");
      return broadband ? broadband.selected : null;
    },
  },
  methods: {
    ...mapActions(["updatePreSignupLeadProductData"]),
    ...mapMutations({
      setProductOption: "SET_PRODUCT_OPTION",
      unPickProductOption: "SET_UNPICKED_PRODUCT_OPTION",
      setPickedProductPrice: "SET_PICKED_PRODUCT_PRICE",
    }),
    updateProductSelection(product) {
      // Find the full product entry in the store
      const fullProductIndex = this.products.findIndex(
        (p) => p.type === product.type,
      );
      const fullProduct = this.products[fullProductIndex];

      if (!fullProduct) {
        console.warn(`No full product found for type: ${product.type}`);
        return;
      }

      let newProductOption;

      if (product.enabled) {
        // Checkbox was checked → uncheck → switch to "_NONE"
        newProductOption = fullProduct.options.find((op) =>
          op.code.includes("_NONE"),
        );
      } else {
        // Checkbox was unchecked → check → pick first paid option
        newProductOption = fullProduct.options.find(
          (op) => !op.code.includes("_NONE"),
        );
      }

      if (!newProductOption) {
        console.warn(`No valid option found for type: ${product.type}`);
        return;
      }

      // Commit to Vuex store
      this.setProductOption({
        type: product.type,
        code: newProductOption.code,
      });

      this.setPickedProductPrice({
        index: fullProductIndex,
        price: newProductOption.price.current,
      });

      this.updatePreSignupLeadProductData(newProductOption.priceId);
    },
    productImage(productOption) {
      if (productOption.image !== "") {
        return productOption.image;
      }

      if (!this.products || this.products.length === 0) {
        return "";
      }

      const product = this.products.find(
        (product) => product.type === productOption.type,
      );
      const option = product.options.find(
        (op) => op.image && op.image.length > 0,
      );
      return option === undefined ? "" : option.image;
    },
    isCellDisabled(product) {
      if (!this.inlineEditing) {
        return false;
      }

      return product.code.includes("_NONE");
    },
    isBroadband(product) {
      const t = product && product.type;
      if (!t) return false;
      if (Array.isArray(t)) return t.includes("Broadband");
      return String(t).includes("Broadband");
    },
    openBroadbandModal(product) {
      this.selectedProduct = product || null;
      this.showBroadbandModal = true;
    },
    closeBroadbandModal: function() {
      this.showBroadbandModal = false;
      this.selectedProduct = null;
    },
    async updateSelected(code) {
      if (!this.selectedProduct) return;

      this.$store.commit("SET_PRODUCT_OPTION", {
        type: this.selectedProduct.type,
        code,
      });

      const selectedOption = this.broadbandProducts.find(
        (op) => op.code === code,
      );
      if (!selectedOption) return;

      const fullProductIndex = this.products.findIndex(
        (p) => p.type === this.selectedProduct.type,
      );

      this.setPickedProductPrice({
        index: fullProductIndex,
        price: selectedOption.price.current,
      });

      try {
        await this.updatePreSignupLeadProductData({
          offerCode: this.$store.state.discount.offerCode,
          affiliateCode: this.$store.state.discount.affiliateCode,
          referralCode: this.$store.state.discount.referralCode,
        });

        if (this.$store.state.discount.isValid) {
          this.$emit("reapply-discount");
        }
      } catch (err) {
        console.error("Failed to update prices:", err);
      }
    },
    priceChanged(price) {
      this.$emit("price-changed", price);
    },
    productInfoLabel(productOption) {
      switch (productOption.type) {
        case "Energy":
          return "No unexpected bills at the end of your tenancy. Make one monthly payment for Gas and Electricity with no worries of extra over-usage payments at the end of your tenancy.";
        case "Water":
          return "Water bills cover both the fresh running water into your home and the sewage system that runs through it.";
        case "TV Licence":
          return "Add a TV licence to your package to make sure your household is legally covered for watching or recording live TV (any channel) and BBC shows (including catch-up on BBC iPlayer).";
      }
    },
    showProductTitle(productType) {
      switch (productType) {
        case "Energy":
          return "Uncapped gas and electricity";

        case "Broadband": {
          const broadbandProduct = this.products.find(
            (p) => p.type === productType,
          );

          if (broadbandProduct && broadbandProduct.selected) {
            const selectedOption = broadbandProduct.options.find(
              (op) => op.code === broadbandProduct.selected,
            );
            return selectedOption ? selectedOption.title : "Broadband";
          }

          return "Broadband";
        }

        case "Water":
          return "Water";

        case "TV Licence":
          return "TV licence";

        default:
          return productType;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.package-panel {
  display: flex;
  flex-direction: column;
  align-self: stretch;
}

.package-panel__products {
  list-style-type: none;
  padding: 0;
  margin: 0;

  &.thin {
    margin: 0 auto;
  }
}

.inline-edit-btn {
  align-self: normal;
  margin: 3px;
  min-width: 25%;
  padding: 0;
  font-size: $form-font-size;
  border-radius: $border-radius-small;
}

.package-panel__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  &.disabled {
    border: 2px solid $grey-dark;
    background: rgba($grey-dark, 0.25);
  }
}

.package-panel__option-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin: 0;
  background: white;
  padding: 0 8px 0 16px;
}

.package-panel__option-image {
  max-width: 28px;
  max-height: 28px;
  object-fit: contain;
}

.package-panel__option-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  gap: 2px;
}

.package-panel__option-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 27px; // keep the heigh consistent even when the info-bubble isn't present
}

.package-panel__option-name {
  margin: 0 5px 0 0;
  color: #3b4143;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }
}

.package-panel__option-price {
  margin: 0;
  color: #3b4143;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  text-align: left;
}

.package-panel__option-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  min-height: 24px;
  min-width: 24px;
}

.product-option__info-btn {
  grid-row: 3;
  grid-column: 1;

  align-self: center;
  justify-self: center;

  text-align: center;
  width: 15px;
  height: 15px;
  font-size: 1.2rem;
  font-weight: 500;
  color: #6b7280;
  background-color: $white;
  border: none;
  border-radius: 50%;
  padding: 0;
  margin-left: 8px;
  cursor: pointer;
}

.form-checkbox {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  appearance: none;
  -webkit-appearance: none;
  background-color: white;
  border: 1px solid $brand-green;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.form-checkbox:checked {
  background-color: $brand-green;
  border-color: $brand-green;
}

.form-checkbox:checked::after {
  content: url("~@/assets/svg/check.svg?external");
}

.edit-broadband {
  display: flex;
  width: 24px;
  height: 24px;
  justify-content: center;
  align-items: center;
  font-size: 2.85rem;
  color: #eb5b2b;
}

.package-info {
  order: 2;

  &__divider {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin-top: 8px;
    width: 100%;
    flex-basis: 100%;
    height: 1px;

    @include media("<large") {
      display: none;
    }
  }

  .package-info-header {
    color: #eb5b2b;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 30px;
    text-align: left;
    margin: 16px 0;

    @include media("<large") {
      font-size: 16px;
      line-height: 24px;
    }
  }

  @include media("<large") {
    order: 1;
  }
}

.no-broadband-icon {
  font-size: 16px;
  font-weight: 300;
  color: #da1c5b;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
